import { useEffect, useMemo, useRef } from "react";
import * as d3 from "d3";

import type { MapDestination } from "../types";
import {
  drawBaseLand,
  drawLegend as drawLegendShared,
  getDestinationDisplayLabel,
  groupDestinationsByCountry,
  loadWorldGeojson,
  MAP_HEIGHT,
  MAP_WIDTH,
  scaleValue,
} from "./mapShared";

type Props = {
  destinations: MapDestination[];
  uiScale?: number;
};

type ClusterDatum = {
  id: string;
  x: number;
  y: number;
  labels: string[];
  shipped: number;
  recovered: number;
  planned: number;
};

type CategoryKey = "shipped" | "recovered" | "planned";

const WIDTH = MAP_WIDTH;
const HEIGHT = MAP_HEIGHT;
const MIN_ZOOM = 1;
const MAX_ZOOM = 16;
const TRANSLATE_PADDING = 0.25;
const INITIAL_FIT_PADDING = 44;
const INITIAL_FIT_SCALE_BOOST = 1.1;

const CATEGORY_CONFIG: Array<{ key: CategoryKey; label: string; color: string }> = [
  { key: "shipped", label: "Shipped", color: "#0f766e" },
  { key: "recovered", label: "Recovered", color: "#f97316" },
  { key: "planned", label: "Planned", color: "#38bdf8" },
];

const SZ = scaleValue;

export function MapReversePanel({ destinations, uiScale = 1 }: Props) {
  const ref = useRef<SVGSVGElement | null>(null);
  const destinationsByCountry = useMemo(() => groupDestinationsByCountry(destinations), [destinations]);

  useEffect(() => {
    if (!ref.current) return;
    const svgNode = ref.current;
    const svg = d3.select(svgNode);
    svg.selectAll("*").remove();
    svg
      .attr("viewBox", `0 0 ${WIDTH} ${HEIGHT}`)
      .style("width", "100%")
      .style("height", `${HEIGHT}px`);

    let alive = true;
    const ac = new AbortController();

    (async () => {
      const geo = await loadWorldGeojson(ac.signal);
      if (!geo || !alive) return;

      const root = svg.append("g").attr("class", "map-root");
      const movable = root.append("g").attr("class", "map-movable");

      const projection = d3.geoMercator().fitExtent(
        [
          [30, 30],
          [WIDTH - 30, HEIGHT - 40],
        ],
        geo,
      );

      drawBaseLand(movable, geo, projection);

      const barsGroup = movable.append("g").attr("class", "map-bars");

      const renderBars = (zoomLevel: number) => {
        barsGroup.selectAll("*").remove();

        const clusters = clusterDestinations(destinationsByCountry, projection, zoomLevel);
        if (!clusters.length) return;

        const maxValue =
          d3.max(clusters, (cluster) =>
            d3.max(CATEGORY_CONFIG.map((category) => cluster[category.key])),
          ) ?? 0;

        if (maxValue <= 0) return;

        const BAR_MAX_HEIGHT = SZ(150, uiScale);
        const BAR_WIDTH = SZ(22, uiScale);
        const BAR_GAP = SZ(10, uiScale);
        const LABEL_OFFSET = SZ(8, uiScale);
        const LABEL_FONT = SZ(13, uiScale);
        const BASE_HEIGHT = SZ(4, uiScale);

        const heightScale = d3.scaleLinear().domain([0, maxValue]).range([0, BAR_MAX_HEIGHT]);
        const BASE_Y = SZ(4, uiScale);

        const groupWidth = CATEGORY_CONFIG.length * BAR_WIDTH + (CATEGORY_CONFIG.length - 1) * BAR_GAP;
        const xOffset = -groupWidth / 2;

        clusters.forEach((cluster) => {
          const g = barsGroup
            .append("g")
            .datum(cluster)
            .attr("class", "bar-item")
            .attr("transform", `translate(${cluster.x}, ${cluster.y}) scale(${1 / zoomLevel})`);

          g.append("title").text(buildTooltip(cluster));

          g
            .append("line")
            .attr("class", "bar-baseline")
            .attr("x1", xOffset - SZ(8, uiScale))
            .attr("x2", xOffset + groupWidth + SZ(8, uiScale))
            .attr("y1", BASE_Y)
            .attr("y2", BASE_Y)
            .attr("stroke", "rgba(15,23,42,0.35)")
            .attr("stroke-width", BASE_HEIGHT)
            .attr("stroke-linecap", "round");

          CATEGORY_CONFIG.forEach((category, index) => {
            const value = cluster[category.key];
            if (!value || value <= 0) return;

            const barHeight = Math.max(heightScale(value), SZ(1.5, uiScale));
            const x = xOffset + index * (BAR_WIDTH + BAR_GAP);

            g.append("rect")
              .attr("class", `bar-rect bar-${category.key}`)
              .attr("x", x)
              .attr("y", BASE_Y - barHeight)
              .attr("width", BAR_WIDTH)
              .attr("height", barHeight)
              .attr("fill", category.color)
              .attr("opacity", 0.9);

            g.append("text")
              .attr("class", "bar-label")
              .attr("x", x + BAR_WIDTH / 2)
              .attr("y", BASE_Y - barHeight - LABEL_OFFSET)
              .attr("text-anchor", "middle")
              .attr("fill", "#0f172a")
              .attr("font-weight", 600)
              .attr("font-size", LABEL_FONT)
              .text(formatValue(value));
          });
        });
      };

      renderBars(1);

      drawLegendShared(svg, CATEGORY_CONFIG, uiScale);

      const zoom = d3
        .zoom<SVGSVGElement, unknown>()
        .scaleExtent([MIN_ZOOM, MAX_ZOOM])
        .translateExtent([
          [WIDTH * -TRANSLATE_PADDING, HEIGHT * -TRANSLATE_PADDING],
          [WIDTH * (1 + TRANSLATE_PADDING), HEIGHT * (1 + TRANSLATE_PADDING)],
        ])
        .extent([
          [0, 0],
          [WIDTH, HEIGHT],
        ])
        .on("zoom", (event) => {
          movable.attr("transform", event.transform.toString());
          renderBars(event.transform.k);
        });

      svg.on(".zoom", null).call(zoom);

      zoomToInitialBounds(svg, zoom, movable);
    })();

    return () => {
      alive = false;
      ac.abort();
    };
  }, [destinationsByCountry, uiScale]);

  return <svg ref={ref} role="img" aria-label="Carte inverse des expéditions" />;
}

function clusterDestinations(
  destinations: MapDestination[],
  projection: d3.GeoProjection,
  zoomLevel: number,
): ClusterDatum[] {
  const CLUSTER_DISTANCE = 140;
  const threshold = CLUSTER_DISTANCE / Math.max(zoomLevel, 0.5);

  const clusters: Array<ClusterDatum & { keys: string[] }> = [];

  destinations.forEach((destination) => {
    if (!destination.destination) return;
    const projected = projection([destination.destination.lon, destination.destination.lat]);
    if (!projected) return;

    const shipped = Math.max(destination.expedition ?? 0, 0);
    const recovered = Math.max(destination.recovered ?? 0, 0);
    const planned = Math.max(Math.round(shipped * 1.125), 0);

    if (shipped === 0 && recovered === 0 && planned === 0) return;

    const label = getDestinationDisplayLabel(destination);

    let cluster = clusters.find((item) => distance(item.x, item.y, projected[0], projected[1]) < threshold);
    if (!cluster) {
      cluster = {
        id: destination.key,
        x: projected[0],
        y: projected[1],
        labels: [label],
        shipped,
        recovered,
        planned,
        keys: [destination.key],
      };
      clusters.push(cluster);
    } else {
      cluster.shipped += shipped;
      cluster.recovered += recovered;
      cluster.planned += planned;
      cluster.labels.push(label);
      cluster.keys.push(destination.key);
      const weight = cluster.labels.length;
      cluster.x = (cluster.x * (weight - 1) + projected[0]) / weight;
      cluster.y = (cluster.y * (weight - 1) + projected[1]) / weight;
    }
  });

  return clusters.map((cluster) => ({
    id: cluster.keys.slice().sort().join("|"),
    x: cluster.x,
    y: cluster.y,
    labels: cluster.labels,
    shipped: cluster.shipped,
    recovered: cluster.recovered,
    planned: cluster.planned,
  }));
}

function zoomToInitialBounds(
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
  zoom: d3.ZoomBehavior<SVGSVGElement, unknown>,
  node: d3.Selection<SVGGElement, unknown, null, undefined>,
) {
  const bbox = (node.node() as SVGGElement).getBBox();
  if (!isFinite(bbox.width) || !isFinite(bbox.height) || bbox.width === 0 || bbox.height === 0) return;

  const PAD = INITIAL_FIT_PADDING;
  const sx = WIDTH / (bbox.width + PAD * 2);
  const sy = HEIGHT / (bbox.height + PAD * 2);
  const scale = Math.max(MIN_ZOOM, Math.min(Math.min(sx, sy) * INITIAL_FIT_SCALE_BOOST, MAX_ZOOM));

  const tx = WIDTH / 2 - scale * (bbox.x + bbox.width / 2);
  const ty = HEIGHT / 2 - scale * (bbox.y + bbox.height / 2);

  const transform = d3.zoomIdentity.translate(tx, ty).scale(scale);
  svg.call(zoom.transform, transform);
}

function distance(x1: number, y1: number, x2: number, y2: number) {
  return Math.hypot(x1 - x2, y1 - y2);
}

function buildTooltip(cluster: ClusterDatum) {
  const title = cluster.labels.length > 3 ? `${cluster.labels.slice(0, 3).join(", ")}...` : cluster.labels.join(", ");
  return `${title}\nShipped : ${formatValue(cluster.shipped)}\nRecovered : ${formatValue(cluster.recovered)}\nPlanned : ${formatValue(cluster.planned)}`;
}

function formatValue(value: number) {
  return value.toLocaleString("fr-FR");
}
